import { useState, useCallback, useEffect } from 'react';
import { validateForm, FieldValidation, ValidationResult, debounce, sanitizers } from '../utils/validation';

interface UseFormValidationOptions {
  validationSchema: FieldValidation;
  initialData?: any;
  sanitizers?: Record<string, (value: any) => any>;
  onSubmit?: (data: any) => void | Promise<void>;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  debounceMs?: number;
}

interface FormField {
  value: any;
  error: string;
  touched: boolean;
  dirty: boolean;
}

interface UseFormValidationReturn {
  formData: any;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  dirty: Record<string, boolean>;
  isValid: boolean;
  isSubmitting: boolean;
  setValue: (field: string, value: any) => void;
  setError: (field: string, error: string) => void;
  clearError: (field: string) => void;
  setTouched: (field: string, touched?: boolean) => void;
  validate: (field?: string) => ValidationResult;
  validateField: (field: string) => string;
  reset: (data?: any) => void;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;
  handleChange: (field: string) => (e: React.ChangeEvent<any>) => void;
  handleBlur: (field: string) => (e: React.FocusEvent<any>) => void;
  getFieldProps: (field: string) => {
    value: any;
    onChange: (e: React.ChangeEvent<any>) => void;
    onBlur: (e: React.FocusEvent<any>) => void;
    error: string | undefined;
    helperText: string;
  };
  fields: Record<string, FormField>;
}

export const useFormValidation = ({
  validationSchema,
  initialData = {},
  sanitizers: fieldSanitizers = {},
  onSubmit,
  validateOnChange = true,
  validateOnBlur = true,
  debounceMs = 300,
}: UseFormValidationOptions): UseFormValidationReturn => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouchedState] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounced validation function
  const debouncedValidate = useCallback(
    debounce((field?: string) => {
      if (field) {
        validateField(field);
      } else {
        validate();
      }
    }, debounceMs),
    [formData, validationSchema]
  );

  // Validate entire form
  const validate = useCallback((): ValidationResult => {
    const result = validateForm(formData, validationSchema);
    setErrors(result.errors);
    return result;
  }, [formData, validationSchema]);

  // Validate single field
  const validateField = useCallback((field: string): string => {
    if (!validationSchema[field]) return '';

    const fieldValue = formData[field];
    let fieldError = '';

    for (const rule of validationSchema[field]) {
      if (!rule.rule(fieldValue, formData)) {
        fieldError = rule.message;
        break;
      }
    }

    setErrors(prev => ({
      ...prev,
      [field]: fieldError,
    }));

    return fieldError;
  }, [formData, validationSchema]);

  // Set field value with optional sanitization
  const setValue = useCallback((field: string, value: any) => {
    let sanitizedValue = value;

    // Apply field-specific sanitizer if available
    if (fieldSanitizers[field]) {
      sanitizedValue = fieldSanitizers[field](value);
    }

    setFormData((prev: any) => ({
      ...prev,
      [field]: sanitizedValue,
    }));

    setDirty(prev => ({
      ...prev,
      [field]: true,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }

    // Validate on change if enabled
    if (validateOnChange && touched[field]) {
      debouncedValidate(field);
    }
  }, [fieldSanitizers, errors, touched, validateOnChange, debouncedValidate]);

  // Set field error
  const setError = useCallback((field: string, error: string) => {
    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  }, []);

  // Clear field error
  const clearError = useCallback((field: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Set field touched state
  const setTouched = useCallback((field: string, isTouched = true) => {
    setTouchedState(prev => ({
      ...prev,
      [field]: isTouched,
    }));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    setIsSubmitting(true);
    
    // Mark all fields as touched
    const allFields = Object.keys(validationSchema);
    const touchedFields = allFields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setTouchedState(touchedFields);

    // Validate entire form
    const result = validate();
    
    if (result.isValid && onSubmit) {
      try {
        await onSubmit(formData);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    }
    
    setIsSubmitting(false);
  }, [formData, validationSchema, validate, onSubmit]);

  // Handle input change
  const handleChange = useCallback((field: string) => {
    return (e: React.ChangeEvent<any>) => {
      const target = e.target;
      let value: any;

      if (target.type === 'checkbox') {
        value = target.checked;
      } else if (target.type === 'file') {
        value = target.files?.[0] || null;
      } else if (target.type === 'number') {
        value = target.value === '' ? '' : Number(target.value);
      } else {
        value = target.value;
      }

      setValue(field, value);
    };
  }, [setValue]);

  // Handle input blur
  const handleBlur = useCallback((field: string) => {
    return (e: React.FocusEvent<any>) => {
      setTouched(field, true);
      
      if (validateOnBlur) {
        validateField(field);
      }
    };
  }, [setTouched, validateOnBlur, validateField]);

  // Get field props for easy integration with form components
  const getFieldProps = useCallback((field: string) => {
    return {
      value: formData[field] || '',
      onChange: handleChange(field),
      onBlur: handleBlur(field),
      error: touched[field] && errors[field] ? errors[field] : undefined,
      helperText: touched[field] && errors[field] ? errors[field] : '',
    };
  }, [formData, touched, errors, handleChange, handleBlur]);

  // Reset form
  const reset = useCallback((data: any = initialData) => {
    setFormData(data);
    setErrors({});
    setTouchedState({});
    setDirty({});
    setIsSubmitting(false);
  }, [initialData]);

  // Create fields object for easy access
  const fields = Object.keys(validationSchema).reduce((acc, field) => {
    acc[field] = {
      value: formData[field],
      error: errors[field] || '',
      touched: touched[field] || false,
      dirty: dirty[field] || false,
    };
    return acc;
  }, {} as Record<string, FormField>);

  // Compute overall form validity
  const isValid = Object.keys(errors).length === 0 && 
                  Object.keys(validationSchema).every(field => 
                    validationSchema[field].every(rule => 
                      rule.rule(formData[field], formData)
                    )
                  );

  return {
    formData,
    errors,
    touched,
    dirty,
    isValid,
    isSubmitting,
    setValue,
    setError,
    clearError,
    setTouched,
    validate,
    validateField,
    reset,
    handleSubmit,
    handleChange,
    handleBlur,
    getFieldProps,
    fields,
  };
};

export default useFormValidation;